/* eslint-disable @typescript-eslint/no-explicit-any */

import Axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import get from 'lodash/get';
import noop from 'lodash/noop';

import {
  authTokenStorage,
} from '@/plugins/auth-token-storage';
import {
  requestRepeatStorage,
} from '@/plugins/request-repeat-storage';
import {
  AuthMutationsTypesEnum,
} from '@/store/modules/auth/mutation-types';
import {
  AxiosParams,
  Nullable,
} from '@/types';

import {
  store,
} from '../store';

let refreshTokenRetryStep = 0;
let refreshTokenPromise: Nullable<Promise<any>> = null;

const resetPromise = (): void => {
  refreshTokenPromise = null;
};

export class AxiosWrapper {
  public instance: AxiosInstance;

  private readonly axiosParams: object;

  private readonly useAuthToken: boolean;

  constructor(params: AxiosParams = {}) {
    const { axios, useAuthToken = true } = params;

    Axios.defaults.baseURL = __config.url.default;
    Axios.defaults.headers.common.Accept = 'application/json';

    this.axiosParams = axios || {};
    this.useAuthToken = useAuthToken;
    this.instance = this.createInstance();
  }

  private createInstance(): AxiosInstance {
    this.instance = Axios.create(this.axiosParams);

    if (this.useAuthToken) {
      this.instance.interceptors.request.use(this.setAccessToken.bind(this));
    }
    this.instance.interceptors.request.use(this.waitRefreshToken.bind(this));
    this.instance.interceptors.request.use(this.checkExpiryToken.bind(this));

    this.instance.interceptors.response.use(this.onResponseFulfill.bind(this), this.onResponseError.bind(this));

    return this.instance;
  }

  private setAccessToken(config: AxiosRequestConfig): AxiosRequestConfig {
    if (config.url && config.url.includes('/token/refresh')) {
      return config;
    }

    const token = authTokenStorage.accessToken;
    if (token && this.useAuthToken) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  }

  private checkExpiryToken(config: AxiosRequestConfig): AxiosRequestConfig {
    const refreshToken = authTokenStorage.refreshToken;
    const expiry = authTokenStorage.expiry;

    if (!refreshToken || !expiry) {
      return config;
    }

    if (!authTokenStorage.isExpired) {
      return config;
    }

    if (!refreshTokenPromise) {
      this.setRefreshTokenPromise();
    }

    return config;
  }

  private async waitRefreshToken(config: AxiosRequestConfig): Promise<AxiosRequestConfig> {
    if (!refreshTokenPromise) {
      return config;
    }

    if (config.url && config.url.includes('/access-token')) {
      return config;
    }

    await this.runRefreshTokenPromise({ message: 'Nonauth' });

    return config;
  }

  private isTokenExpired(error: AxiosError): boolean {
    const { response } = error;

    // TODO: Дождаться от @roquie response.status.code с константой об истекшем токене
    return !!response && response.status === 401;
  }

  private onResponseFulfill(response: AxiosResponse): AxiosResponse {
    return response;
  }

  private async onResponseError(error: AxiosError): Promise<any> {
    const responseStatus = get(error, 'response.status');
    const isAuthError = [401, 403].includes(responseStatus);
    const isTokenExpired = this.isTokenExpired(error);

    if (!isAuthError && !isTokenExpired) {
      return this.baseResponseErrorHandler(error);
    }

    if (!store.getters['auth/isAuthenticated']) {
      throw error;
    }

    const refreshToken = authTokenStorage.refreshToken;

    if (!isTokenExpired || !refreshToken) {
      this.logoutHandler();
      throw error;
    }

    if (refreshTokenRetryStep >= __config.requests.refreshTokenRetries) {
      this.logoutHandler();
      throw error;
    }

    if (!refreshTokenPromise) {
      this.setRefreshTokenPromise();
    }

    await this.runRefreshTokenPromise(error);

    return this.instance.request(error.config);
  }

  private baseResponseErrorHandler(error: AxiosError): any {
    const responseStatus = get(error, 'response.status');
    const method = get(error, 'config.method').toLowerCase();
    const requestUrl = error.config.url || '';

    if (!requestRepeatStorage.hasRequest(requestUrl)) {
      requestRepeatStorage.rememberRequest(requestUrl);
    }

    if (method === 'get'
      && responseStatus >= 500
      && requestRepeatStorage.getRequestTry(requestUrl) < __config.requests.hiddenRetries
    ) {
      requestRepeatStorage.incrementRequestTry(requestUrl || '');

      return new Promise((resolve: any) => {
        setTimeout(() => {
          resolve(this.instance(error.config));
        }, __config.requests.hiddenRetriesTimeout);
      });
    }

    return Promise.reject(error);
  }

  private setRefreshTokenPromise(): void {
    refreshTokenPromise = store.dispatch('auth/refreshToken');
  }

  private async runRefreshTokenPromise(error: any): Promise<void> {
    if (!refreshTokenPromise) {
      return;
    }

    const result = await refreshTokenPromise.catch(noop);

    refreshTokenRetryStep++;
    resetPromise();

    if (!result) {
      this.logoutHandler();
      throw error;
    }
  }

  private logoutHandler(): void {
    refreshTokenRetryStep = 0;

    if (location.pathname === '/') {
      authTokenStorage.clear();

      store.commit(`auth/${AuthMutationsTypesEnum.UPDATE_AUTH_STATUS}`, false);
      store.dispatch('profile/reset');

      return;
    }

    store.dispatch('auth/logout', `/login?redirect_to=${location.pathname}`);
  }
}
